import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../data/types';
import { ScreenSection, ScreenChip, ScreenH3 } from '../primitives/ScreenPrimitives';
import { ScreenStatGrid, ScreenMiniBars, ScreenDonut } from '../primitives/ChartPrimitives';
import { colors } from '../data/designTokens';
import {
    DEBT_DETAILS_MODULE_TITLE,
    MCA_CHARGES_SUBSECTION_TITLE,
    REPORT_ANCHOR_ATTR,
    reportAnchorId,
    SUB_OPTION_CHARGE_DETAILS_ID,
    subOptionOn,
    REPORT_MODULE_ID,
} from '../constants';
import styles from './McaCharges.module.scss';

const statusTone = (status: string) => (status === 'OPEN' ? 'HIGH' : 'LOW');

const ScreenMcaCharges: React.FC<{ data: ReportData; index: string; options?: Record<string, boolean> }> = ({ data, index, options }) => {
    const mc = data.mcaCharges;
    if (!mc) return null;
    const showChargeDetails = subOptionOn(options, SUB_OPTION_CHARGE_DETAILS_ID);
    const byHolder = new Map<string, number>();
    mc.rows.filter((r) => r.status === 'OPEN').forEach((r) => byHolder.set(r.holder, (byHolder.get(r.holder) ?? 0) + r.amountNumeric));
    const donutSlices = Array.from(byHolder.entries()).map(([label, value]) => ({ label, value }));
    const barItems = mc.rows.slice(0, 6).map((r) => ({ label: r.holder, value: r.amountNumeric, valueLabel: r.amount }));
    return (
        <ScreenSection kicker="Section · Charges" title={DEBT_DETAILS_MODULE_TITLE} index={index}>
            <ScreenH3 subtitle="Creditor-wise open & closed charges filed against the company." topGap={0}>
                {MCA_CHARGES_SUBSECTION_TITLE}
            </ScreenH3>
            <ScreenStatGrid
                items={[
                    { label: 'Total charges', value: String(mc.rows.length) },
                    { label: 'Open charges', value: String(mc.totalOpen), deltaTone: mc.totalOpen > 0 ? 'neg' : 'pos' },
                    { label: 'Open exposure', value: mc.totalOpenAmountLabel },
                    { label: 'Closed', value: String(mc.totalClosed) },
                ]}
            />
            {donutSlices.length > 0 ? (
                <div style={{ marginTop: 14, padding: 12, background: colors.surfaceAlt, borderRadius: 6 }}>
                    <div
                        style={{
                            fontSize: 10,
                            letterSpacing: 1,
                            color: colors.muted,
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 3,
                        }}
                    >
                        Open exposure by creditor
                    </div>
                    <div style={{ fontSize: 10.5, color: colors.mutedSoft, marginBottom: 8, lineHeight: 1.4 }}>
                        Share of total outstanding (open) charge amount held by each lender.
                    </div>
                    <ScreenDonut slices={donutSlices} size={110} />
                </div>
            ) : null}
            <div style={{ marginTop: 14 }}>
                <div
                    style={{
                        fontSize: 10,
                        letterSpacing: 1,
                        color: colors.muted,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        marginBottom: 6,
                    }}
                >
                    Top charges (by amount)
                </div>
                <ScreenMiniBars items={barItems} />
            </div>
            {showChargeDetails ? (
                <div
                    className={styles.Charges}
                    {...{ [REPORT_ANCHOR_ATTR]: reportAnchorId(REPORT_MODULE_ID.mcaCharges, SUB_OPTION_CHARGE_DETAILS_ID) }}
                >
                    {mc.rows.map((r, i) => (
                        <div key={i} className={styles.ChargeRow}>
                            <div className={styles.ChargeHeadline}>
                                <div className={styles.Holder}>{r.holder}</div>
                                <div className={styles.Amount}>{r.amount}</div>
                                <div className={styles.CreationDate}>{r.creationDate}</div>
                                <div className={styles.StatusCell}>
                                    <ScreenChip tone={statusTone(r.status)}>{r.status}</ScreenChip>
                                </div>
                            </div>
                            <div className={styles.ChipRow}>
                                {r.rateOfInterest ? <ScreenChip tone="muted">RoI {r.rateOfInterest}</ScreenChip> : null}
                                {r.numberOfHolders && r.numberOfHolders !== '1' ? (
                                    <ScreenChip tone="muted">{r.numberOfHolders} holders</ScreenChip>
                                ) : null}
                                {r.modificationDate ? <ScreenChip tone="muted">Mod. {r.modificationDate}</ScreenChip> : null}
                                {r.propertyType && r.propertyType.length <= 32 ? (
                                    <ScreenChip tone="muted">{r.propertyType}</ScreenChip>
                                ) : null}
                                {r.instrumentDesc ? <ScreenChip tone="muted">{r.instrumentDesc}</ScreenChip> : null}
                            </div>
                            {r.propertyType && r.propertyType.length > 32 ? (
                                <div className={styles.LongPropertyType}>{r.propertyType}</div>
                            ) : null}
                            {(r.termsOfPayment || r.propertyParticulars || r.extentAndOperation || r.otherTerms) && (
                                // Always expanded: a collapsed <details> drops its body from the server PDF capture.
                                <div className={styles.Terms}>
                                    <div className={styles.TermsHeading}>Charge terms</div>
                                    <div className={styles.TermsBody}>
                                        {r.termsOfPayment ? (
                                            <div className={styles.TermLine}>
                                                <strong>Payment terms:</strong> {r.termsOfPayment}
                                            </div>
                                        ) : null}
                                        {r.propertyParticulars ? (
                                            <div className={styles.TermLine}>
                                                <strong>Particulars:</strong> {r.propertyParticulars}
                                            </div>
                                        ) : null}
                                        {r.extentAndOperation ? (
                                            <div className={styles.TermLine}>
                                                <strong>Extent & operation:</strong> {r.extentAndOperation}
                                            </div>
                                        ) : null}
                                        {r.otherTerms ? (
                                            <div>
                                                <strong>Other terms:</strong> {r.otherTerms}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : null}
        </ScreenSection>
    );
};

export const mcaChargesModule: ModuleDefinition = {
    meta: {
        id: 'mcaCharges' as any,
        title: DEBT_DETAILS_MODULE_TITLE,
        description: 'Secured lender exposure, creditor concentration and open / closed status.',
        dataSource: 'polus/insights/tab/data:mcaCharges',
    },
    hasData: (d) => !!d.mcaCharges && d.mcaCharges.rows.length > 0,
    Screen: ScreenMcaCharges,
};
